/**
 * Where a card happens: the set's places, for the card's where line and the
 * sheet's showings grouped by cinema. Cards plan WP4 (item D of
 * `docs/cards-plan/README.md`).
 *
 * `feed.places` (WP3, 20261001100000) is one element per venue over the
 * canonical's **and its twins'** future performance showings, ordered by that
 * venue's next showing: `{venue_id, venue, building_id, building, count,
 * next_at, versions}`. It is never null from the view — `[]` when the set has
 * nothing ahead — but **a row cached in IndexedDB before WP3 has no `places`
 * key at all**, and `feed_page` re-sends a row only when its `updated_at`
 * moves (WP3 moved it on canonicals with twins and nothing else). That is
 * CLAUDE.md's E5 trap exactly, so nothing here reads `row.places` bare:
 * `placesOf` is the one door, and it answers `[]` for anything that is not an
 * array. `[]` then means "nothing to say", and every caller falls back to
 * what it printed before WP4 — the row's own venue and the showings it holds.
 *
 * Dependency-free on purpose (CLAUDE.md: a decision in a file with no imports
 * is a decision `npm test` can reach). `rowMeta.ts` imports it with its
 * `.ts` extension, as `chatClient.ts` imports `rowMeta.ts`, so that file
 * stays loadable by Node's test runner too.
 */

/** One element of `places`, as `feed_lite` sends it. */
export interface Place {
  venue_id: string
  venue: string | null
  /** The parent venue, else the venue itself — `showing_list`'s coalesce,
   *  so it is never null from the view; optional here for a hand-built row. */
  building_id?: string | null
  building?: string | null
  /** Distinct start instants at this venue. */
  count?: number | null
  next_at?: string | null
  /** `showings.note` as the house printed it (OV, OmU, OmeU, DF), sorted. */
  versions?: string[] | null
}

/** Anything that may carry `places` — `FeedRow`, `RowEntry`, a stale row. */
export interface MaybePlaces { places?: readonly Place[] | null }

/** The row's places, or `[]`. The only read of `places` in the app. */
export function placesOf(row: MaybePlaces | null | undefined): readonly Place[] {
  const p = row?.places
  return Array.isArray(p) ? p : []
}

/** A building over its halls: what someone deciding where to go means by
 *  "a cinema" (`cinemaCount`'s own rule — a multiplex is one cinema). */
export interface Building {
  id: string
  name: string
  next_at: string | null
  versions: string[]
}

const buildingKey = (p: Place): string => p.building_id ?? p.venue_id

/**
 * The place page a venue name opens (a1-28): the building's, since a hall is
 * a room in one and the place page is keyed on the building — `placesOf`'s
 * own key. Null where there is no venue row to open (a newsletter lead whose
 * place matched none).
 */
export function placeIdOf(x: { building_id?: string | null; venue_id?: string | null } | null | undefined): string | null {
  return x?.building_id ?? x?.venue_id ?? null
}
const earlier = (a: string | null | undefined, b: string | null | undefined): string | null =>
  !a ? b ?? null : !b ? a : a < b ? a : b

/**
 * `places` collapsed to buildings, in the order the view sent them — which is
 * by next showing, so the first building is where the set plays next. Halls
 * of one building merge: their versions are unioned (sorted, as the view sorts
 * them) and the earliest `next_at` stands.
 */
export function buildingsOf(places: readonly Place[]): Building[] {
  const out: Building[] = []
  const at = new Map<string, Building>()
  for (const p of places) {
    if (!p || !p.venue_id) continue
    const key = buildingKey(p)
    const versions = Array.isArray(p.versions) ? p.versions : []
    const seen = at.get(key)
    if (seen) {
      seen.next_at = earlier(seen.next_at, p.next_at)
      seen.versions = [...new Set([...seen.versions, ...versions])].sort()
      continue
    }
    const b: Building = { id: key, name: p.building ?? p.venue ?? key, next_at: p.next_at ?? null, versions: [...versions] }
    at.set(key, b)
    out.push(b)
  }
  return out
}

/** How many named places a where line prints before it counts them instead.
 *  Two, because rule 3 of the card says nothing truncates the venue and the
 *  line wraps rather than ellipsising: Berlin's cinemas are called "CineStar
 *  Cubix am Alexanderplatz" and "Kino in der KulturBrauerei", and three of
 *  those in bold is three lines of a 390px card before the showing count. */
export const NAMED_PLACES = 2

/**
 * The where line's place, when the card is a set: the buildings named
 * (`Zoo Palast, UCI Luxe Mercedes Platz`) up to `NAMED_PLACES`, else counted
 * (`10 cinemas`). Null when the set is one building or none — the caller
 * then prints the row's own venue exactly as before WP4, so a card with one
 * place does not change at all.
 *
 * `noun` is the caller's word for the count — `cinemas` for a film — because
 * this file does not know what kind of row it is looking at.
 */
export function setPlaceText(places: readonly Place[], noun: string): string | null {
  const b = buildingsOf(places)
  if (b.length < 2) return null
  if (b.length <= NAMED_PLACES) return b.map(x => x.name).join(', ')
  return `${b.length} ${noun}`
}

/**
 * The places of a card that stands for several rows (`mergeFilmRuns`: two
 * canonicals of one film that the twins pass never joined — an unmatched
 * film, say). Halls are merged by venue, counts added, the earliest `next_at`
 * kept, versions unioned, then ordered by next showing as the view orders.
 *
 * **Undefined when any member's `places` is not an array** — a member cached
 * before WP3. A merged set built from the members that happen to carry the
 * column would claim "3 cinemas" for a film at five; undefined sends the
 * card back to the pre-WP4 line, which is old but not wrong.
 */
export function mergePlaces(lists: readonly (readonly Place[] | null | undefined)[]): Place[] | undefined {
  if (lists.some(l => !Array.isArray(l))) return undefined
  const by = new Map<string, Place>()
  for (const list of lists as readonly (readonly Place[])[]) {
    for (const p of list) {
      if (!p || !p.venue_id) continue
      const seen = by.get(p.venue_id)
      if (!seen) {
        by.set(p.venue_id, { ...p, versions: [...(p.versions ?? [])] })
        continue
      }
      seen.count = (seen.count ?? 0) + (p.count ?? 0)
      seen.next_at = earlier(seen.next_at, p.next_at)
      seen.versions = [...new Set([...(seen.versions ?? []), ...(p.versions ?? [])])].sort()
    }
  }
  return [...by.values()].sort((a, b) => {
    const x = a.next_at ?? '￿', y = b.next_at ?? '￿'
    return x < y ? -1 : x > y ? 1 : a.venue_id < b.venue_id ? -1 : a.venue_id > b.venue_id ? 1 : 0
  })
}

/**
 * How many showings a card has ahead of it, for the count it prints and the
 * one-showing clock rule ("Fri 2 Oct, 19:30" only when there is exactly one).
 *
 * **A film reads `places`, everything else `showing_count`.** Since cards
 * WP1 a film row keeps its past weeks' showings, and `showing_count` counts
 * every performance showing, past ones included — so on a film it grows week
 * by week and stops meaning "how many are left". `places[].count` is future
 * only and over the whole set, so a film with a non-empty `places` sums it.
 * A film with `places` absent (cached before WP3) or `[]` falls back to
 * `showing_count`, as before; a non-film is untouched, because only film
 * rows keep their past (cards plan item A).
 *
 * The same value doing two jobs is this codebase's recurring bug (CLAUDE.md):
 * `showing_count` was "how many in all" for the Worker and "how many left"
 * for the card, and WP1 made the two disagree on every film.
 */
export function aheadCount(e: MaybePlaces & {
  showing_count?: number | null; type?: string | null; discipline?: string | null
}): number {
  const film = e.type === 'screening' || e.type === 'release' || e.discipline === 'film'
  const p = film ? placesOf(e) : []
  if (p.length) return p.reduce((n, x) => n + (typeof x?.count === 'number' ? x.count : 0), 0)
  return e.showing_count ?? 0
}

// ---------------------------------------------------------------------------
// The sheet: showings grouped by place
// ---------------------------------------------------------------------------

/** One showing, structurally — `ShowingLite` satisfies it. */
export interface PlacedShowing {
  starts_at: string
  venue_id?: string | null
  building_id?: string | null
  venue?: string | null
  kind?: string | null
  note?: string | null
}

/**
 * One showing once. `showing_list` resolves a twin's showings to its
 * canonical, so where two sources list the same night (b-ware is in The
 * Odyssey's set twice) the sheet received it twice: 47 chips for 37 nights,
 * and two chips with one React key. The same night is the same venue at the
 * same instant; the first copy stands. A showing with neither venue field
 * keys on its instant alone.
 *
 * The first copy stands, but a version note only a later copy printed is
 * kept (UI-audit a1-11): VATERLAND's Il Kino nights arrive twice, once with
 * `OmeU` and once with nothing, and keeping the bare copy made the sheet say
 * "OmeU on 6 dates" beside eleven cinemas that all play it OmeU. A source
 * that did not print the version is not a source saying there is none.
 */
export function uniqueShowings<S extends PlacedShowing>(showings: readonly S[]): S[] {
  const at = new Map<string, number>()
  const out: S[] = []
  for (const s of showings) {
    const key = `${s.venue_id ?? s.venue ?? ''}|${new Date(s.starts_at).getTime()}`
    const i = at.get(key)
    if (i === undefined) {
      at.set(key, out.length)
      out.push(s)
    } else if (!out[i].note && s.note) {
      out[i] = { ...out[i], note: s.note }
    }
  }
  return out
}

export interface PlaceGroup<S> {
  /** The building id, or the venue's where a showing has no building. */
  key: string
  /** The building's name from `places`, else the showing's own venue, else
   *  the caller's fallback (the row's own place). */
  name: string
  /** Earliest first, one copy of each night. */
  showings: S[]
}

/**
 * The sheet's showings grouped by building, in `places` order — by next
 * showing, so the cinema playing soonest is first — with any building the
 * row's `places` does not list (a stale cached row, a showing added since)
 * after them, by its own first showing. Within a group, earliest first.
 *
 * The caller passes the occurrences it means to draw (future, no `run`
 * markers); this decides only the grouping and the order, and returns one
 * group for a single-building row — the sheet then draws its chips exactly
 * as before WP4.
 */
export function groupByPlace<S extends PlacedShowing>(
  showings: readonly S[],
  places: readonly Place[],
  fallbackName = '',
): PlaceGroup<S>[] {
  const buildings = buildingsOf(places)
  const rank = new Map(buildings.map((b, i) => [b.id, i]))
  const named = new Map(buildings.map(b => [b.id, b.name]))
  // Each building's own id, and each of its halls' venue ids, to the
  // building — so a showing whose `building_id` the client does not have
  // (a cache older than that column) still lands under its building.
  const toBuilding = new Map<string, string>(buildings.map(b => [b.id, b.id]))
  for (const p of places) if (p?.venue_id && !toBuilding.has(p.venue_id)) toBuilding.set(p.venue_id, buildingKey(p))

  const groups = new Map<string, PlaceGroup<S>>()
  const sorted = uniqueShowings(showings).sort((a, b) => (a.starts_at < b.starts_at ? -1 : a.starts_at > b.starts_at ? 1 : 0))
  for (const s of sorted) {
    const key = (s.building_id && toBuilding.get(s.building_id))
      ?? (s.venue_id && toBuilding.get(s.venue_id))
      ?? s.building_id ?? s.venue_id ?? s.venue ?? ''
    const g = groups.get(key)
    if (g) { g.showings.push(s); continue }
    groups.set(key, { key, name: named.get(key) ?? s.venue ?? fallbackName, showings: [s] })
  }

  const all = [...groups.values()]
  const pos = (g: PlaceGroup<S>) => rank.get(g.key) ?? Number.MAX_SAFE_INTEGER
  // Stable: groups `places` does not rank keep the order of their first
  // showing, which is the order they were created in above.
  return all.sort((a, b) => pos(a) - pos(b))
}

// ---------------------------------------------------------------------------
// The desk pane: the showings it lists to commit to
// ---------------------------------------------------------------------------

/** How many showings the desk pane lists before "N more showings". */
export const PANE_SHOWINGS = 14

export interface PaneShowings<S> {
  /** True when the showings are at more than one building: each group then
   *  draws under its cinema's name, and the lines need not repeat it. */
  grouped: boolean
  /** One group, unnamed, when not `grouped`. */
  groups: PlaceGroup<S>[]
  /** Every night once, the ones not drawn included. */
  total: number
  /** How many `all` would add. */
  more: number
}

/**
 * The desk pane's "Commit to a date" list (UI-audit a3-07, a3-08): the soonest
 * `PANE_SHOWINGS` nights, or every one with `all`, grouped by cinema exactly
 * as the phone sheet groups them (`groupByPlace`) when the set plays more than
 * one building. Whether it is grouped is asked of every night, not only the
 * ones drawn — the soonest fourteen can all be at one cinema of eleven, and
 * then the one header is what says which.
 *
 * Before, the pane drew fourteen lines and an inert "71 more showings", so a
 * long-running film's later weeks could not be committed to at all.
 */
export function paneShowings<S extends PlacedShowing>(
  showings: readonly S[],
  places: readonly Place[],
  opts: { all?: boolean; limit?: number; fallbackName?: string } = {},
): PaneShowings<S> {
  const limit = opts.limit ?? PANE_SHOWINGS
  const name = opts.fallbackName ?? ''
  const one = uniqueShowings(showings).sort((a, b) => (a.starts_at < b.starts_at ? -1 : a.starts_at > b.starts_at ? 1 : 0))
  const shown = opts.all ? one : one.slice(0, limit)
  const grouped = groupByPlace(one, places, name).length > 1
  const groups = grouped ? groupByPlace(shown, places, name) : shown.length ? [{ key: '', name, showings: shown }] : []
  return { grouped, groups, total: one.length, more: one.length - shown.length }
}

// ---------------------------------------------------------------------------
// The phone sheet: how many cinemas it draws before "N more cinemas"
// ---------------------------------------------------------------------------

/** UI-audit a1-30, Doug chose: the two soonest cinemas, then the rest behind
 *  one control. */
export const SHEET_PLACES = 2

/**
 * Which of `groupByPlace`'s groups the phone sheet draws, and how many it
 * folds away. `groups` arrive soonest first (`places` is ordered by next
 * showing), so the first `keep` are the two soonest cinemas.
 *
 * Two things keep a group on screen whatever its position:
 *
 * - `open` — the rest were asked for, or the sheet is asking for a date
 *   (calendar-pick-date): a picker that hides nine cinemas' nights cannot
 *   pick them, so asking opens every one.
 * - `holds` — the group with the highlighted night. That chip is the night
 *   Where, Tickets and Calendar follow; folding it away would leave the sheet
 *   describing a choice it is not showing.
 *
 * Folding a single group behind "1 more cinema" saves nothing and costs a
 * tap, so a fold of one is not made.
 */
export function foldPlaces<G>(
  groups: readonly G[],
  opts: { open?: boolean; keep?: number; holds?: (g: G) => boolean } = {},
): { shown: G[]; hidden: number } {
  const keep = opts.keep ?? SHEET_PLACES
  if (opts.open || groups.length <= keep + 1) return { shown: [...groups], hidden: 0 }
  const shown = groups.filter((g, i) => i < keep || !!opts.holds?.(g))
  const hidden = groups.length - shown.length
  return hidden > 1 ? { shown, hidden } : { shown: [...groups], hidden: 0 }
}

/** "9 more cinemas", "2 more venues". */
export const morePlacesText = (n: number, noun: 'cinemas' | 'venues'): string =>
  `${n} more ${n === 1 ? noun.slice(0, -1) : noun}`
